import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Heart, Share, ChevronLeft, MapPin, Calendar, 
  Users, Info, Star, Tent, Zap, Droplet, Wifi, Globe,
  Map as MapIcon, ArrowRight, Music, X, Sparkles, Loader2, ShieldCheck, Mail, Home, CheckCircle2, AlertCircle,
  Instagram, Facebook, Linkedin, Youtube, Twitter, ChevronDown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, addDoc, query, where, getDocs, onSnapshot, deleteDoc, serverTimestamp, setDoc, arrayUnion } from 'firebase/firestore';
import { createNotification } from '../components/NotificationBell';
import { RatingBadge } from '../components/RatingBadge';
import { detectContactInfo, CONTACT_BLOCK_MESSAGE } from '../utils/contactFilter';
import toast from 'react-hot-toast';

interface PitchFormat {
  id: string;
  width: string;
  depth: string;
  flooring: string;
  price: string;
}

interface Festival {
  id: string;
  ownerId: string;
  name: string;
  organizerName?: string;
  category: string;
  location: string;
  startDate: string;
  endDate: string;
  capacity?: number;
  dailyPax?: number;
  terrainType?: string;
  description?: string;
  about?: string;
  accessInfo?: string;
  audienceProfile?: string;
  editionNumber?: string | number;
  pastAudience?: string;
  historyDescription?: string;
  ticketingUrl?: string;
  allowedSlots?: string[];
  musicStyles?: string[];
  totalStands?: number;
  customTimeRange?: string;
  customCategory?: string;
  standTypes?: string[];
  standDetails?: Record<string, StandDetail>;
  standCounts?: Record<string, number>;
  technical?: {
    powerKva: number;
    has16A: boolean;
    has32A: boolean; 
    has63A: boolean;
    hasDrinkingWater: boolean;
    wastewaterDetails: string;
    providesTables: boolean;
    nightSecurity?: string;
    pitches: PitchFormat[];
    arrivalDate?: string;
    arrivalTime?: string;
    setupStartTime?: string;
    setupEndTime?: string;
    departureTime?: string;
    applicationDeadline?: string;
    equipmentList?: string[];
    parkingOption?: string;
    securityOption?: string;
    wasteManagement?: string;
  };
  insuranceUrl?: string;
  branding?: {
    description: string;
    coverImage: string;
    logoImage: string;
    gallery: string[];
    highlights?: string[];
    socials?: {
      instagram?: string;
      facebook?: string;
      tiktok?: string;
      linkedin?: string;
      website?: string;
      youtube?: string;
      twitter?: string;
    };
  };
}

interface StandDetail {
  dimensionType: string;
  customDimension: string;
  hasElectricity: boolean;
  hasWater?: boolean;
  isFree?: boolean;
  budget: number;
  note: string;
}

export default function FestivalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [festival, setFestival] = useState<Festival | null>(null);
  const [loading, setLoading] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [approvedCount, setApprovedCount] = useState(0);
  const [contacting, setContacting] = useState(false);
  const [standCompleteness, setStandCompleteness] = useState({ score: 0, loading: true, data: null as any });
  const [mapCoords, setMapCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; standType: string; label: string; price: number | string; isFree: boolean; dimensions: string; hasElectricity: boolean; hasWater: boolean; accent: string } | null>(null);
  const [motivation, setMotivation] = useState('');
  const [motivationError, setMotivationError] = useState('');

  useEffect(() => {
    if (!festival?.location) return;
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(festival.location)}&format=json&limit=1`)
      .then(r => r.json())
      .then(data => { if (data[0]) setMapCoords({ lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) }); })
      .catch(() => {});
  }, [festival?.location]);

  useEffect(() => {
    if (!id) return;
    const q = query(
      collection(db, 'applications'),
      where('festivalId', '==', id),
      where('status', '==', 'approved')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setApprovedCount(snapshot.size);
    }, (error) => {
      console.error("Error listening to approved applications in FestivalDetailPage:", error);
    });
    return () => unsubscribe();
  }, [id]);

  useEffect(() => {
    const fetchFestivalAndStatus = async () => {
      if (!id) return;
      
      const docRef = doc(db, 'festivals', id);
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() } as Festival;
          console.log("Festival data fetched:", data);
          setFestival(data);
          setLoading(false);
        }
      });
      
      // Need a separate check for 'hasApplied' if we don't want to do that in onSnapshot
      if (user) {
        const q = query(
          collection(db, 'applications'),
          where('festivalId', '==', id),
          where('applicantId', '==', user.uid)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          setHasApplied(true);
        }
      }
      return () => unsubscribe();
    };
    fetchFestivalAndStatus();
  }, [id, user]);

  useEffect(() => {
    const checkStandProfile = async () => {
      if (!user) {
        setStandCompleteness({ score: 0, loading: false, data: null });
        return;
      }
      try {
        const standDoc = await getDoc(doc(db, 'stands', user.uid));
        if (standDoc.exists()) {
          const data = standDoc.data();
          // Fields to check: name, standType, description, logo/images, length/depth, electricity
          const essentialFields = [
            !!data.name,
            !!data.standType,
            !!data.region,
            !!data.description,
            !!(data.logo || (data.images && data.images.length > 0)),
            !!(data.length && data.depth),
            !!data.electricity
          ];
          const completedCount = essentialFields.filter(Boolean).length;
          const score = (completedCount / essentialFields.length) * 100;
          setStandCompleteness({ score, loading: false, data });
        } else {
          setStandCompleteness({ score: 0, loading: false, data: null });
        }
      } catch (err) {
        console.error("Error checking stand profile", err);
        setStandCompleteness({ score: 0, loading: false, data: null });
      }
    };
    checkStandProfile();
  }, [user]);

  useEffect(() => {
    if (!user || !id) {
      setIsFavorite(false);
      return;
    }

    const q = query(
      collection(db, 'favorites'), 
      where('userId', '==', user.uid),
      where('festivalId', '==', id)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setIsFavorite(!snapshot.empty);
    }, (error) => {
      console.error("Error fetching favorite status:", error);
    });

    return () => unsubscribe();
  }, [id, user]);

  const toggleFavorite = async () => {
    if (!user) {
      toast.error("Veuillez vous connecter pour ajouter des favoris.");
      return;
    }
    if (!id) return;

    try {
      if (isFavorite) {
        const q = query(
          collection(db, 'favorites'), 
          where('userId', '==', user.uid),
          where('festivalId', '==', id)
        );
        const snap = await getDocs(q);
        for (const fDoc of snap.docs) {
          await deleteDoc(doc(db, 'favorites', fDoc.id));
        }
        toast.success("Retiré des favoris");
      } else {
        await addDoc(collection(db, 'favorites'), {
          userId: user.uid,
          festivalId: id,
          createdAt: serverTimestamp()
        });
        toast.success("Ajouté aux favoris !");
      }
    } catch (err) {
      console.error("Toggle favorite failed:", err);
      toast.error("Erreur favorite");
    }
  };

  const handleContact = async () => {
    if (!user) {
      toast.error("Veuillez vous connecter pour contacter l'organisateur");
      return;
    }

    if (!festival || !festival.ownerId) {
      toast.error("Impossible de contacter l'organisateur pour le moment");
      return;
    }

    if (festival.ownerId === user.uid) {
      toast.error("C'est votre propre festival");
      return;
    }

    setContacting(true);
    try {
      const q = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', user.uid),
        where('isGroup', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      let existingConv = querySnapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(festival.ownerId);
      });

      if (existingConv) {
        navigate('/messages', { state: { conversationId: existingConv.id } });
      } else {
        const newConv = {
          name: festival.name,
          avatar: festival.branding?.logoImage || "",
          participants: [user.uid, festival.ownerId],
          participantDetails: {
            [user.uid]: { name: user.displayName || 'Utilisateur', avatar: user.photoURL || '' },
            [festival.ownerId]: { name: festival.name, avatar: festival.branding?.logoImage || '' }
          },
          isGroup: false,
          lastMessage: "Conversation démarrée",
          updatedAt: serverTimestamp(),
          role: 'festival',
          unread: 0
        };

        const docRef = await addDoc(collection(db, 'conversations'), newConv);
        navigate('/messages', { state: { conversationId: docRef.id } });
      }
    } catch (error) {
      console.error("Error starting conversation:", error);
      toast.error("Erreur lors de l'ouverture de la messagerie");
    } finally {
      setContacting(false);
    }
  };

  const handleApply = async (messageMotivation: string = '') => {
    if (!user) {
      toast.error("Veuillez vous connecter pour postuler.");
      return;
    }
    // Défense en profondeur : rejette les coordonnées même si l'UI est contournée.
    if (messageMotivation && detectContactInfo(messageMotivation).hasContact) {
      toast.error(CONTACT_BLOCK_MESSAGE);
      return;
    }
    if (!festival) return;

    if (festival.ownerId === user.uid) {
      toast.error("Vous ne pouvez pas postuler à votre propre festival.");
      return;
    }

    setIsApplying(true);
    try {
      const standId = user.uid; 
      
      // Fetch stand data for the current user
      const standDoc = await getDoc(doc(db, 'stands', user.uid));
      if (!standDoc.exists()) {
        toast.error("Vous devez créer un profil de stand pour postuler.");
        setIsApplying(false);
        return;
      }
      
      const standData = standDoc.data();
      const standCategory = standData.standType || 'Autre';

      // Parrainage exposant : on recopie le code du parrain (porté par le doc
      // user du candidat) sur la candidature, pour que le parrain retrouve ses
      // filleuls dans son onglet Parrainage (/api/stands/:id/referral-stats).
      let referredByStand = '';
      try {
        const applicantDoc = await getDoc(doc(db, 'users', user.uid));
        referredByStand = applicantDoc.exists() ? (applicantDoc.data().referredByStand || '') : '';
      } catch { /* noop — non bloquant */ }

      const format = festival.technical?.pitches?.[0] ? `${festival.technical.pitches[0].width}x${festival.technical.pitches[0].depth}m` : 'Format de base';

      const newApp = {
        festivalId: festival.id,
        festivalOwnerId: festival.ownerId,
        standId: standId,
        applicantId: user.uid,
        festivalName: festival.name,
        location: festival.location,
        dates: `${new Date(festival.startDate).toLocaleDateString('fr-FR')} - ${new Date(festival.endDate).toLocaleDateString('fr-FR')}`,
        standName: standData.name || user.displayName || "Mon Stand",
        applicantName: user.displayName || user.email || "Utilisateur",
        applicantEmail: user.email,
        format: format,
        category: standCategory,
        status: 'pending',
        appliedAt: new Date().toISOString(),
        ...(referredByStand ? { referredByStand } : {}),
        ...(messageMotivation ? { messageMotivation: messageMotivation.slice(0, 800) } : {})
      };

      await addDoc(collection(db, 'applications'), newApp);

      // Accorde à l'organisateur du festival l'accès en lecture aux documents
      // légaux privés du stand (KBIS, assurance, sanitaire). Les règles Firestore
      // ne pouvant pas requêter `applications`, on matérialise ce droit via la
      // liste allowedViewers du doc privé. Écriture autorisée car l'auteur est le
      // propriétaire du stand (user.uid === standId).
      try {
        await setDoc(
          doc(db, 'stands', user.uid, 'private', 'documents'),
          { allowedViewers: arrayUnion(festival.ownerId) },
          { merge: true }
        );
      } catch (grantErr) {
        console.error("Impossible d'accorder l'accès aux documents légaux à l'organisateur", grantErr);
      }

      await createNotification({
        userId: festival.ownerId,
        title: "Nouvelle candidature !",
        message: `Le stand "${newApp.standName}" a postulé pour "${festival.name}".`,
        type: 'info',
        link: `/manage-festival/${festival.id}`
      });

      setHasApplied(true);
      toast.success("Candidature envoyée !");
    } catch (err: any) {
      console.error(err);
      toast.error("Erreur lors de la candidature");
    } finally {
      setIsApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white font-sans">
        <Loader2 className="w-8 h-8 text-[#FF5C00] animate-spin" />
      </div>
    );
  }

  if (!festival) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white px-6 font-sans">
        <h1 className="text-2xl font-bold mb-4">Festival introuvable</h1>
        <button onClick={() => navigate('/home')} className="text-[#FF5C00] font-semibold underline flex items-center">
          <ChevronLeft className="w-4 h-4 mr-1" /> Retour à l'accueil
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-sans relative overflow-x-hidden pb-32">
      

      <button 
        onClick={() => navigate(-1)} 
        className="fixed top-6 left-6 z-50 p-3 bg-black/50 border border-white/10 rounded-full text-white backdrop-blur-md hover:bg-white/10 transition-all shadow-xl"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      {/* Hero — style Shotgun : 2 colonnes, image contenue */}
      <div className="relative overflow-hidden pt-20 md:pt-28 pb-10 md:pb-16">
        {/* Ambiance : cover floutée en fond */}
        <div className="absolute inset-0 z-0">
          <img
            src={festival.branding?.coverImage || "https://images.unsplash.com/photo-1540039155733-d7696d4ebc59?auto=format&fit=crop&q=80"}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-20 blur-2xl scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

            {/* Colonne infos */}
            <div className="order-2 lg:order-1">
              <span className="inline-block mb-4 px-3 py-1 bg-white/[0.06] text-gray-300 rounded-full text-xs font-medium border border-white/10">
                {{
                  music: 'Musique',
                  culture: 'Culturel & Artistique',
                  convention: 'Convention & Pop-Culture',
                  food: 'Gastronomie & Terroir',
                  fair: 'Foire, Salon & Exposition',
                  market: 'Marché Éphémère & Pop-up',
                  sport: 'Sport & Outdoor',
                  local: 'Fête Locale',
                  other: festival.customCategory || 'Autre'
                }[festival.category] || festival.category || 'Général'}
              </span>

              <h1 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-3 break-words">
                {festival.name}
              </h1>
              <div className="mb-3">
                <RatingBadge rating={(festival as any).rating} />
              </div>
              {festival.insuranceUrl && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl mb-3">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">Assuré RC Pro</span>
                </div>
              )}
              {festival.organizerName && (
                <p className="text-gray-400 text-sm md:text-base mb-6">Par {festival.organizerName}</p>
              )}

              <div className="flex items-start gap-3 py-4 border-t border-white/[0.07]">
                <Calendar className="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
                <div className="text-sm md:text-base text-gray-200 leading-relaxed">
                  <div>
                    <span className="text-gray-500">Du </span>
                    {festival.startDate ? new Date(festival.startDate).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) : "Dates à venir"}
                  </div>
                  {festival.endDate && (
                    <div>
                      <span className="text-gray-500">au </span>
                      {new Date(festival.endDate).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3 py-4 border-t border-white/[0.07]">
                <MapPin className="w-5 h-5 text-gray-500 mt-0.5 shrink-0" />
                <div className="text-sm md:text-base text-gray-200">{festival.location}</div>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-7">
                {festival.ticketingUrl && (
                  <a
                    href={festival.ticketingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-black hover:bg-gray-100 px-6 h-12 rounded-xl font-semibold text-sm flex items-center justify-center transition-all active:scale-95 shadow-lg"
                  >
                    <Globe className="w-4 h-4 mr-2" /> Billetterie
                  </a>
                )}
                {user?.uid !== festival.ownerId && (
                  <button
                    onClick={handleContact}
                    disabled={contacting}
                    className="px-6 h-12 rounded-xl font-semibold text-sm flex items-center justify-center transition-all disabled:opacity-50 active:scale-95 bg-white/[0.06] hover:bg-white/[0.10] text-white border border-white/15"
                  >
                    <Mail className="w-4 h-4 mr-2" /> {contacting ? "..." : "Contacter"}
                  </button>
                )}
                <button
                  onClick={toggleFavorite}
                  className={`h-12 w-12 flex items-center justify-center rounded-xl border transition-all active:scale-95 ${
                    isFavorite
                    ? 'bg-red-500/15 border-red-500/30 text-red-400'
                    : 'bg-white/[0.06] border-white/15 text-white hover:bg-white/[0.10]'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
                </button>
                <button
                  onClick={async () => {
                    const url = window.location.href;
                    const text = festival?.name || 'Festival';
                    if (navigator.share) {
                      try { await navigator.share({ title: text, url }); } catch {}
                    } else {
                      await navigator.clipboard.writeText(url);
                      toast.success('Lien copié !');
                    }
                  }}
                  className="h-12 w-12 flex items-center justify-center rounded-xl border border-white/15 bg-white/[0.06] text-white hover:bg-white/[0.10] transition-all active:scale-95"
                >
                  <Share className="w-5 h-5" />
                </button>
              </div>

              {festival.branding?.socials && Object.values(festival.branding.socials).some(v => v) && (
                <div className="flex items-center gap-2 mt-5">
                  {festival.branding.socials.instagram && (
                    <a href={festival.branding.socials.instagram.startsWith('http') ? festival.branding.socials.instagram : `https://instagram.com/${festival.branding.socials.instagram.replace('@','')}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/[0.05] hover:bg-white/[0.12] rounded-lg border border-white/10 transition-all text-gray-300 hover:text-white">
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                  {festival.branding.socials.facebook && (
                    <a href={festival.branding.socials.facebook.startsWith('http') ? festival.branding.socials.facebook : `https://${festival.branding.socials.facebook}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/[0.05] hover:bg-white/[0.12] rounded-lg border border-white/10 transition-all text-gray-300 hover:text-white">
                      <Facebook className="w-4 h-4" />
                    </a>
                  )}
                  {festival.branding.socials.tiktok && (
                    <a href={festival.branding.socials.tiktok.startsWith('http') ? festival.branding.socials.tiktok : `https://tiktok.com/${festival.branding.socials.tiktok.startsWith('@') ? festival.branding.socials.tiktok : '@' + festival.branding.socials.tiktok}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/[0.05] hover:bg-white/[0.12] rounded-lg border border-white/10 transition-all text-gray-300 hover:text-white">
                      <Music className="w-4 h-4" />
                    </a>
                  )}
                  {festival.branding.socials.youtube && (
                    <a href={festival.branding.socials.youtube.startsWith('http') ? festival.branding.socials.youtube : `https://${festival.branding.socials.youtube}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/[0.05] hover:bg-white/[0.12] rounded-lg border border-white/10 transition-all text-gray-300 hover:text-white">
                      <Youtube className="w-4 h-4" />
                    </a>
                  )}
                  {festival.branding.socials.linkedin && (
                    <a href={festival.branding.socials.linkedin.startsWith('http') ? festival.branding.socials.linkedin : `https://${festival.branding.socials.linkedin}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/[0.05] hover:bg-white/[0.12] rounded-lg border border-white/10 transition-all text-gray-300 hover:text-white">
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {festival.branding.socials.website && (
                    <a href={festival.branding.socials.website.startsWith('http') ? festival.branding.socials.website : `https://${festival.branding.socials.website}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/[0.05] hover:bg-white/[0.12] rounded-lg border border-white/10 transition-all text-gray-300 hover:text-white">
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Colonne image (contenue, nette) */}
            <div className="order-1 lg:order-2">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-white/[0.08] shadow-[0_10px_40px_-15px_rgba(0,0,0,0.9)] bg-[#1A1A1A]">
                <img
                  src={festival.branding?.coverImage || "https://images.unsplash.com/photo-1540039155733-d7696d4ebc59?auto=format&fit=crop&q=80"}
                  alt={festival.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="px-4 md:px-16 mt-2 md:mt-4 relative z-20 max-w-7xl mx-auto space-y-10 md:space-y-16">
         
         {/* Key Figures */}
         <div className="grid grid-cols-3 bg-[#141414] border border-white/[0.07] rounded-2xl shadow-[0_8px_24px_-12px_rgba(0,0,0,0.7)] divide-x divide-white/[0.06] overflow-hidden">
             <div className="p-4 md:p-7 flex flex-col items-center justify-center text-center">
                <span className="text-2xl md:text-4xl font-semibold text-white tracking-tight mb-1.5">
                  {festival.dailyPax?.toLocaleString() || "0"}
                </span>
                <span className="text-gray-500 text-[11px] md:text-sm">Festivaliers / jour</span>
             </div>
             <div className="p-4 md:p-7 flex flex-col items-center justify-center text-center">
                <span className="text-lg md:text-2xl font-semibold text-white tracking-tight mb-1.5 line-clamp-1">
                  {festival.terrainType || "Extérieur"}
                </span>
                <span className="text-gray-500 text-[11px] md:text-sm">Site</span>
             </div>
             <div className="p-4 md:p-7 flex flex-col items-center justify-center text-center">
                <span className="text-2xl md:text-4xl font-semibold text-white tracking-tight mb-1.5">
                  {festival.totalStands || "0"}
                </span>
                <span className="text-gray-500 text-[11px] md:text-sm">Stands prévus</span>
             </div>
         </div>

         <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
           
           {/* Left Column */}
           <div className="lg:col-span-8 space-y-10 md:space-y-16">
              
              {/* Description / About */}
              <div>
                <h2 className="text-2xl font-semibold text-white mb-6 group flex items-center">
                  L'Événement <Info className="w-5 h-5 ml-2 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h2>
                <p className={`text-gray-300 text-lg leading-relaxed whitespace-pre-wrap mb-2 ${!isDescriptionExpanded ? 'line-clamp-[10]' : ''}`}>
                  {festival.about || "L'histoire et les valeurs de l'événement."}
                </p>
                {!isDescriptionExpanded && (festival.about?.length || 0) > 300 && (
                   <button onClick={() => setIsDescriptionExpanded(true)} className="text-[#FF5C00] flex items-center mb-10">
                     <ChevronDown className="w-8 h-8"/>
                   </button>
                )}

                {festival.audienceProfile && (
                  <div className="mb-10">
                    <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-gray-500" /> Profil du public
                    </h2>
                    <div className="bg-[#141414] border border-white/[0.07] p-6 rounded-2xl">
                      <p className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap">
                        {festival.audienceProfile}
                      </p>
                    </div>
                  </div>
                )}

                {(festival.editionNumber || festival.pastAudience || festival.historyDescription) && (
                  <div className="mb-10">
                    <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                      <Star className="w-5 h-5 mr-2 text-gray-500" /> Historique & notoriété
                    </h2>
                    <div className="bg-[#141414] border border-white/[0.07] p-6 md:p-8 rounded-2xl">

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                        {festival.editionNumber && (
                          <div>
                            <p className="text-[10px] text-gray-500 font-semibold mb-1">Édition actuelle</p>
                            <p className="text-2xl font-semibold text-white">{festival.editionNumber}ème édition</p>
                          </div>
                        )}
                        {festival.pastAudience && (
                          <div>
                            <p className="text-[10px] text-gray-500 font-semibold mb-1">Impact passés</p>
                            <p className="text-2xl font-semibold text-white">{festival.pastAudience}</p>
                          </div>
                        )}
                      </div>

                      {festival.historyDescription && (
                        <div>
                          <p className="text-[10px] text-gray-500 font-semibold mb-2">Faits marquants</p>
                          <p className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap italic">
                            "{festival.historyDescription}"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <Sparkles className="w-5 h-5 mr-2 text-gray-500" /> L'appel d'offres
                </h2>
                <div className="bg-white/[0.03] border-l-2 border-[#FF5C00]/70 p-6 rounded-r-xl mb-10">
                  <p className="text-gray-400 text-base leading-relaxed whitespace-pre-wrap italic">
                    "{festival.description || "Détails de l'appel pour les stands."}"
                  </p>
                </div>

                {/* Créneaux Horaires */}
                {festival.allowedSlots && festival.allowedSlots.length > 0 && (
                  <div className="mb-10">
                    <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                      <Zap className="w-5 h-5 mr-2 text-gray-500" /> Créneaux horaires & logistique
                    </h2>
                    <div className="flex flex-wrap gap-3">
                      {festival.allowedSlots.map((slot: string) => (
                        <span key={slot} className="px-4 py-2 bg-white/[0.06] text-gray-300 border border-white/10 rounded-full text-xs font-medium">
                          {slot}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Access Info */}
              {festival.accessInfo && (
                <div>
                  <h2 className="text-2xl font-semibold text-white mb-6">Accès & Mobilité</h2>
                  <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-6 md:p-8 text-gray-300">
                     <p className="leading-relaxed">{festival.accessInfo}</p>
                  </div>
                </div>
              )}

              {/* Gallery integration */}
              {festival.branding?.gallery && festival.branding.gallery.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold text-white mb-6">Galerie</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {festival.branding.gallery.map((img, idx) => (
                      <div key={idx} className="aspect-square rounded-2xl overflow-hidden border border-white/5 hover:border-white/20 transition-all cursor-zoom-in">
                        <img src={img} alt={`Gallery ${idx}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" />
                      </div>
                    ))}
                  </div>
                </div>
              )}



           </div>

           {/* Right Column (Sidebar) */}
           <div className="lg:col-span-4 space-y-8">
             
             {/* Logistique (Bleue) */}
             <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-6 md:p-8 relative overflow-hidden shadow-[0_8px_24px_-12px_rgba(0,0,0,0.7)]">
              <h3 className="text-xl font-semibold text-white mb-8 flex items-center">
                 Logistique <Zap className="w-5 h-5 ml-2 text-gray-500" />
               </h3>
               <ul className="space-y-8">
                  <li className="flex items-start">
                    <div className="p-3 bg-white/[0.05] rounded-xl mr-4 shrink-0 border border-white/10">
                      <Zap className="w-6 h-6 text-gray-300" />
                    </div>
                    <div>
                       <div className="font-bold text-white mb-1">Énergie & Fluides</div>
                       <div className="text-xs text-gray-400 leading-relaxed">
                        {festival.technical?.powerKva || 0}kVA. 
                        {[
                          festival.technical?.has16A && '16A',
                          festival.technical?.has32A && '32A',
                          festival.technical?.has63A && '63A'
                        ].filter(Boolean).join(', ') || 'Standard.'}
                        <br/>
                        Eau potable: {festival.technical?.hasDrinkingWater ? 'Oui' : 'Non'}. 
                        {festival.technical?.wastewaterDetails || ''}
                       </div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="p-3 bg-white/[0.05] rounded-xl mr-4 shrink-0 border border-white/10">
                      <Tent className="w-6 h-6 text-gray-300" />
                    </div>
                    <div>
                       <div className="font-bold text-white mb-1">Planning Logistique</div>
                       <div className="text-xs text-gray-400 leading-relaxed">
                        Arrivée: {festival.technical?.arrivalDate || 'N/A'} {festival.technical?.arrivalTime || ''}
                        <br/>
                        Installation: {festival.technical?.setupStartTime || ''} - {festival.technical?.setupEndTime || ''}
                        <br/>
                        Départ: {festival.technical?.departureTime || 'N/A'}
                       </div>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="p-3 bg-white/[0.05] rounded-xl mr-4 shrink-0 border border-white/10">
                      <ShieldCheck className="w-6 h-6 text-gray-300" />
                    </div>
                    <div>
                       <div className="font-bold text-white mb-1">Services & Sécurité</div>
                       <div className="text-xs text-gray-400 leading-relaxed">
                        Parking: {festival.technical?.parkingOption || 'N/A'}
                        <br/>
                        Gardiennage: {festival.technical?.nightSecurity || festival.technical?.securityOption || 'N/A'}
                        <br/>
                        Déchets: {festival.technical?.wasteManagement || 'N/A'}
                       </div>
                    </div>
                  </li>
                </ul>
              </div>

             {/* Emplacements Disponibles (Booking Cards) */}
             <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Offres Actives</h3>
                </div>

                {festival.totalStands === 0 && (
                  <div className="flex items-center gap-3 bg-red-950/40 border border-red-500/30 rounded-2xl px-5 py-4">
                    <span className="text-red-400 text-lg">🔴</span>
                    <div>
                      <p className="text-red-300 font-bold text-sm">Ce festival affiche complet</p>
                      <p className="text-red-400/70 text-xs mt-0.5">Les candidatures sont fermées — aucune place disponible.</p>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-4">
                  {festival.allowedSlots?.map((slotId: string) => {
                     const labels: any = { 
                       day: 'Journée', 
                       night: 'Nuit', 
                       full_pass: 'Full Pass',
                       custom: festival.customTimeRange || 'Plage libre'
                     };
                     return (
                       <span key={slotId} className="px-3 py-1.5 bg-white/[0.06] text-gray-300 border border-white/10 rounded-full text-[11px] font-medium hover:bg-white/[0.1] transition-colors cursor-default">
                         {labels[slotId] || slotId}
                       </span>
                     )
                  })}
                </div>

                {festival.standTypes && festival.standTypes.length > 0 ? (() => {
                  const typeLabels: Record<string, string> = {
                    food: 'Food & Restauration', snacking: 'Snacking / Boissons',
                    craft: 'Artisanat / Créateurs', fashion: 'Mode / Friperie',
                    vinyls: 'Vinyles / Disques', tattoo: 'Tatouage / Piercing',
                    associative: 'Associatif / Engagé', partners: 'Partenaires / Sponsors',
                    service: 'Services / Bien-être', other: 'Autre',
                    music: 'Musique', art: 'Art & Exposition',
                  };
                  const typeAccent: Record<string, string> = {
                    food: '#f97316', snacking: '#fb923c', craft: '#a855f7',
                    fashion: '#ec4899', vinyls: '#6366f1', tattoo: '#14b8a6',
                    associative: '#22c55e', partners: '#3b82f6', service: '#06b6d4',
                    other: '#6b7280', music: '#FF5C00', art: '#8b5cf6',
                  };

                  const cards = festival.standTypes.flatMap((standTypeId) => {
                    const count = festival.standCounts?.[standTypeId] || 0;
                    return Array.from({ length: count }).map((_, idx) => {
                      const instanceId = `${standTypeId}-${idx}`;
                      const details = festival.standDetails?.[instanceId];
                      if (!details) return null;
                      const accent = typeAccent[standTypeId] || '#6b7280';
                      const label = typeLabels[standTypeId] || standTypeId;
                      const dim = details.dimensionType === 'Sur-mesure' ? details.customDimension : details.dimensionType;
                      const isComplet = festival.totalStands === 0;
                      const isDisabled = isApplying || hasApplied || standCompleteness.score < 60 || isComplet;

                      return (
                        <div
                          key={instanceId}
                          className="relative bg-[#141414] border border-white/[0.07] rounded-2xl overflow-hidden hover:border-white/15 transition-all"
                          style={{ borderLeft: `3px solid ${accent}` }}
                        >
                          <div className="p-5 md:p-6">
                            {/* Header */}
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h4 className="text-base font-bold text-white leading-tight">{label}</h4>
                                {count > 1 && (
                                  <p className="text-[11px] text-gray-500 mt-0.5">Place {idx + 1} sur {count}</p>
                                )}
                              </div>
                              <span
                                className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full"
                                style={{ background: `${accent}20`, color: accent }}
                              >
                                {count} place{count > 1 ? 's' : ''}
                              </span>
                            </div>

                            {/* Badges équipements */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              {dim && (
                                <span className="flex items-center gap-1 bg-white/5 border border-white/10 text-gray-300 text-[11px] font-medium px-2.5 py-1 rounded-full">
                                  <MapIcon className="w-3 h-3" /> {dim}
                                </span>
                              )}
                              {details.hasElectricity && (
                                <span className="flex items-center gap-1 bg-[#FF5C00]/10 border border-[#FF5C00]/20 text-[#FF5C00] text-[11px] font-medium px-2.5 py-1 rounded-full">
                                  <Zap className="w-3 h-3" /> Électricité
                                </span>
                              )}
                              {details.hasWater && (
                                <span className="flex items-center gap-1 bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[11px] font-medium px-2.5 py-1 rounded-full">
                                  <Droplet className="w-3 h-3" /> Point d'eau
                                </span>
                              )}
                            </div>

                            {/* Note orga */}
                            {details.note && (
                              <p className="text-[11px] text-gray-400 italic bg-white/5 border border-white/5 rounded-xl px-4 py-3 mb-4">
                                "{details.note}"
                              </p>
                            )}

                            {/* Prix + CTA */}
                            <div className="flex items-center justify-between pt-4 border-t border-white/5">
                              <div>
                                {details.isFree ? (
                                  <span className="text-emerald-400 font-bold text-lg">Gratuit</span>
                                ) : (
                                  <div className="flex items-baseline gap-1">
                                    <span className="text-2xl font-black text-white">{details.budget}€</span>
                                    <span className="text-xs text-gray-500 font-medium">/jour</span>
                                  </div>
                                )}
                              </div>
                              <button
                                disabled={isDisabled}
                                onClick={isDisabled ? undefined : () => {
                                  setMotivation('');
                                  setMotivationError('');
                                  setConfirmModal({
                                    isOpen: true,
                                    standType: standTypeId,
                                    label,
                                    price: details.isFree ? 'Gratuit' : details.budget,
                                    isFree: details.isFree || false,
                                    dimensions: dim || '',
                                    hasElectricity: details.hasElectricity,
                                    hasWater: details.hasWater || false,
                                    accent
                                  });
                                }}
                                className="px-5 py-2.5 rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                                style={isDisabled ? {} : { background: accent, color: '#fff' }}
                              >
                                {isComplet ? 'Complet' : hasApplied ? 'Déjà postulé' : 'Postuler'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  }).filter(Boolean);

                  return cards.length > 0 ? cards : (
                    <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-6 text-center text-gray-500">
                      Aucun emplacement configuré.
                    </div>
                  );
                })() : (
                  <div className="bg-[#141414] border border-white/[0.07] rounded-2xl p-6 text-center text-gray-500">
                    Aucun emplacement configuré.
                  </div>
                )}
             </div>


           </div>
         </div>
      </div>

       {/* Localisation */}
       <div className="px-4 md:px-16 mt-12 md:mt-20 max-w-7xl mx-auto pb-12 md:pb-20">
         <h2 className="text-2xl md:text-3xl font-semibold text-white mb-6 md:mb-8">Localisation</h2>

         <div className="flex items-center gap-4 py-4 border-t border-white/[0.07]">
           <Home className="w-5 h-5 text-gray-400 shrink-0" />
           <span className="text-base md:text-lg text-white">{festival.name}</span>
         </div>
         <div className="flex items-center gap-4 py-4 border-t border-white/[0.07] mb-6">
           <MapPin className="w-5 h-5 text-gray-400 shrink-0" />
           <span className="text-base md:text-lg text-white">{festival.location}</span>
         </div>

         <div className="w-full h-[280px] md:h-[420px] rounded-2xl overflow-hidden border border-white/[0.08]">
           {mapCoords ? (
             <iframe
               width="100%"
               height="100%"
               frameBorder="0"
               style={{ border: 0 }}
               src={`https://www.openstreetmap.org/export/embed.html?bbox=${mapCoords.lon - 0.01},${mapCoords.lat - 0.007},${mapCoords.lon + 0.01},${mapCoords.lat + 0.007}&layer=mapnik&marker=${mapCoords.lat},${mapCoords.lon}`}
               allowFullScreen
               loading="lazy"
               className="w-full h-full"
               title="Localisation du festival"
             />
           ) : (
             <div className="w-full h-full flex items-center justify-center bg-white/5 text-gray-500 text-sm font-medium">
               Chargement de la carte…
             </div>
           )}
         </div>

         <button
           onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(festival.location)}`, '_blank')}
           className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
         >
           Obtenir l'itinéraire <ArrowRight className="w-4 h-4" />
         </button>
       </div>

      {/* Modal de confirmation avec affichage du prix */}
      {confirmModal?.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-[#141414] border border-white/10 rounded-2xl max-w-md w-full shadow-[0_20px_60px_-15px_rgba(0,0,0,0.9)]">
            {/* Header */}
            <div className="p-6 border-b border-white/5">
              <h2 className="text-2xl font-black text-white flex items-center">
                <AlertCircle className="w-6 h-6 mr-3" style={{ color: confirmModal.accent }} />
                Confirmer la candidature
              </h2>
            </div>

            {/* Contenu */}
            <div className="p-6 space-y-6">
              {/* Nom du stand */}
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Type d'emplacement</p>
                <p className="text-lg font-bold text-white">{confirmModal.label}</p>
              </div>

              {/* Détails */}
              <div className="space-y-3 bg-white/5 border border-white/5 rounded-xl p-4">
                {confirmModal.dimensions && (
                  <div className="flex items-start gap-3">
                    <MapIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-300">{confirmModal.dimensions}</span>
                  </div>
                )}
                {confirmModal.hasElectricity && (
                  <div className="flex items-start gap-3">
                    <Zap className="w-4 h-4 text-[#FF5C00] mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-300">Électricité incluse</span>
                  </div>
                )}
                {confirmModal.hasWater && (
                  <div className="flex items-start gap-3">
                    <Droplet className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                    <span className="text-sm text-gray-300">Point d'eau inclus</span>
                  </div>
                )}
              </div>

              {/* Prix (mise en avant) */}
              <div className="bg-gradient-to-br from-[#FF5C00]/10 to-[#FF5C00]/5 border border-[#FF5C00]/20 rounded-xl p-4">
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Tarif TTC</p>
                <p className="text-3xl font-black text-white">
                  {confirmModal.isFree ? (
                    <span className="text-emerald-400">Gratuit</span>
                  ) : (
                    <>
                      {confirmModal.price}€ <span className="text-sm text-gray-400 font-normal">/jour</span>
                    </>
                  )}
                </p>
              </div>

              {/* Message de motivation (facultatif) */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Message à l'organisateur (facultatif)</label>
                <textarea
                  value={motivation}
                  onChange={(e) => { setMotivation(e.target.value.slice(0, 800)); if (motivationError) setMotivationError(''); }}
                  rows={3}
                  maxLength={800}
                  placeholder="Un mot pour l'organisateur ? Expliquez pourquoi votre stand serait un bon fit pour ce festival (facultatif)."
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#FF5C00] resize-none"
                />
                <div className="flex justify-between items-center mt-1">
                  {motivationError
                    ? <p className="text-xs text-red-400 leading-snug pr-2">{motivationError}</p>
                    : <span />}
                  <span className="text-[11px] text-gray-600 shrink-0">{motivation.length}/800</span>
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div className="p-6 border-t border-white/5 flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  const check = detectContactInfo(motivation);
                  if (check.hasContact) {
                    setMotivationError(CONTACT_BLOCK_MESSAGE);
                    return;
                  }
                  const msg = motivation.trim();
                  setConfirmModal(null);
                  await handleApply(msg);
                }}
                className="flex-1 px-4 py-3 rounded-xl text-sm font-bold text-white transition-all active:scale-95"
                style={{ background: confirmModal.accent || '#FF5C00' }}
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
